import { useCallback, useEffect, useMemo, useRef } from "react";
import { Button } from "components/common/buttons/Button";
import { useAlertDialog } from "context/AlertDialogContext";
import { BudgetCategory } from "../types";
import { regenerableCategories } from "../targetedRerun";
import type { RebudgetBlock } from "../useRebudgetGate";
import { ChooseCategoriesDialog } from "./ChooseCategoriesDialog";

interface TargetedRebudgetBannerProps {
  /** Top-level budget categories, as on the top sheet. */
  categories: BudgetCategory[];
  onRebudgetCategories: (categoryIds: number[]) => void;
  /** Opens the full rerun flow, the same one as the page's Rebudget button. */
  onUpdateAll: () => void;
  /**
   * Why this person cannot rebudget, from the same gate as the header's Rebudget button. While
   * set, the notice has no actions; it adds the header's wording except for viewers.
   */
  rerunBlock?: RebudgetBlock | null;
  /** Viewers get no explanation for any block, even one that names another reason first. */
  isViewer?: boolean;
  /** A run is active or a request is out: both actions are unavailable. */
  isPending?: boolean;
  /** This banner's request is out: the dialog action shows progress and the choice is locked. */
  isSubmitting?: boolean;
  /**
   * API note when the saved assumptions were adjusted so a rerun accepts them. Targeted
   * rebudget re-sends those adjusted values, so show it and confirm before spending credits.
   */
  rerunParamsNotice?: string | null;
  /**
   * What a rerun costs, worded like the full rerun dialog (see `budgetRerunCostLine`). Null on
   * plans that include AI, where the dialog shows no cost either.
   */
  costLine: string | null;
  /** Why the backend refused the last request before charging, e.g. no room under the maximum. */
  refusal?: string | null;
  /**
   * Owned by the page, like the dialog's open state, so both survive the banner hiding while a
   * request is checked and the refusal still shows beside the choice that caused it.
   */
  selectedCategoryIds: number[];
  onSelectedCategoryIdsChange: (categoryIds: number[]) => void;
  isChooseCategoriesOpen: boolean;
  onChooseCategoriesOpenChange: (open: boolean) => void;
}

export const TargetedRebudgetBanner = ({
  categories,
  onRebudgetCategories,
  onUpdateAll,
  rerunBlock = null,
  isViewer = false,
  isPending = false,
  isSubmitting = false,
  rerunParamsNotice,
  costLine,
  refusal,
  selectedCategoryIds,
  onSelectedCategoryIdsChange,
  isChooseCategoriesOpen,
  onChooseCategoriesOpenChange,
}: TargetedRebudgetBannerProps) => {
  const { showAlertDialog, hideAlertDialog } = useAlertDialog();
  const chooseButtonRef = useRef<HTMLButtonElement>(null);
  // The adjusted-assumptions confirmation lives in the app-wide alert, outside this banner. It
  // must close with the chooser, or it could send categories the page no longer shows. Closing
  // by its id leaves any other alert alone, including one that has since replaced it.
  const confirmIdRef = useRef<number | null>(null);
  const hideAlertDialogRef = useRef(hideAlertDialog);
  hideAlertDialogRef.current = hideAlertDialog;
  const closeConfirm = useCallback(() => {
    if (confirmIdRef.current === null) return;
    hideAlertDialogRef.current(confirmIdRef.current);
    confirmIdRef.current = null;
  }, []);
  const isChooserShown = isChooseCategoriesOpen && !rerunBlock;
  useEffect(() => {
    if (!isChooserShown) closeConfirm();
  }, [isChooserShown, closeConfirm]);
  // The banner itself hides while another run is active.
  useEffect(() => closeConfirm, [closeConfirm]);

  const choices = useMemo(() => regenerableCategories(categories), [categories]);

  // Only ids that still exist on the sheet and AI can regenerate are counted and sent.
  const selectedIds = useMemo(() => {
    const selectable = new Set(choices.map((category) => category.budget_category_id));
    return selectedCategoryIds.filter((id) => selectable.has(id));
  }, [choices, selectedCategoryIds]);

  const openChooser = () => {
    // Every visit starts with nothing picked, which also clears a refusal about an old choice.
    onSelectedCategoryIdsChange([]);
    onChooseCategoriesOpenChange(true);
  };

  const toggleCategory = (categoryId: number) => {
    onSelectedCategoryIdsChange(
      selectedIds.includes(categoryId)
        ? selectedIds.filter((id) => id !== categoryId)
        : [...selectedIds, categoryId]
    );
  };

  const handleRebudget = () => {
    const categoryIds = selectedIds;
    if (!rerunParamsNotice) {
      onRebudgetCategories(categoryIds);
      return;
    }
    confirmIdRef.current = showAlertDialog({
      title: "Rebudget with adjusted assumptions?",
      type: "warning",
      message: (
        <div className="flex flex-col gap-2">
          <p>{rerunParamsNotice}</p>
          <p>
            {costLine ? `${costLine} ` : ""}To change these values first, open Budget assumptions
            and save.
          </p>
        </div>
      ),
      actions: {
        cancel: {
          text: "Cancel",
          // Also runs on Escape, a click outside and the close button.
          onCancel: () => {
            confirmIdRef.current = null;
          },
        },
        success: {
          text: "Rebudget with these values",
          onSuccess: () => {
            confirmIdRef.current = null;
            onRebudgetCategories(categoryIds);
          },
        },
      },
    });
  };

  return (
    <section
      className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-regular-3 text-muted-foreground"
      aria-label="Script changed"
    >
      <p className="m-0 min-w-0">
        The script changed after the last AI budget.
        {rerunBlock && !isViewer && rerunBlock.kind !== "viewer" ? ` ${rerunBlock.message}` : null}
      </p>
      {rerunBlock ? null : (
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="badge"
            color="brand"
            disabled={isPending}
            onClick={onUpdateAll}
          >
            Update all
          </Button>
          {choices.length > 0 ? (
            <Button
              ref={chooseButtonRef}
              type="button"
              size="sm"
              variant="badge"
              color="gray"
              disabled={isPending}
              onClick={openChooser}
            >
              Choose categories
            </Button>
          ) : null}
        </div>
      )}
      <ChooseCategoriesDialog
        open={isChooseCategoriesOpen && choices.length > 0 && !rerunBlock}
        onClose={() => onChooseCategoriesOpenChange(false)}
        categories={choices}
        selectedCategoryIds={selectedIds}
        onToggleCategory={toggleCategory}
        onSubmit={handleRebudget}
        isPending={isPending}
        isSubmitting={isSubmitting}
        rerunParamsNotice={rerunParamsNotice}
        costLine={costLine}
        refusal={refusal}
        returnFocusTo={() => chooseButtonRef.current}
      />
    </section>
  );
};
